import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ScrollText, Users as UsersIcon, Gavel, Palette, BadgeCheck, Star, Send, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TILES = [
  { to: "/settings?tab=team",       label: "Team members",        Icon: UsersIcon, blurb: "Add and remove people on your hiring team. Plan inherits from the company owner." },
  { to: "/settings?tab=branding",   label: "Branding",            Icon: Palette,   blurb: "Customise your careers page, logo, and primary colour. Growth+ / Enterprise." },
  { to: "/settings?tab=moderation", label: "Moderation history",  Icon: Gavel,     blurb: "Warnings, bans, limits, and appeals on your account." },
  { to: "/settings?tab=billing",    label: "Billing & boosts",    Icon: Star,      blurb: "Subscriptions, invoices, and free boosts redeemed this month." },
  { to: "/settings?tab=trackers",   label: "Custom Trackers",     Icon: Clock,     blurb: "View custom progression, reviews, or customized payment trackers." },
  { to: "/recruiter?tab=pipeline",  label: "Pipeline & roles",    Icon: BadgeCheck, blurb: "Your open roles, applicants, and post-interview workflow." },
  { to: "/disclaimer",              label: "Legal & disclaimer",  Icon: ScrollText, blurb: "Platform terms — you, your team, and applicants all rely on these." },
];

const OWNER_TILES = [
  { to: "/admin?tab=users",        label: "All users",           Icon: UsersIcon, blurb: "Promote / demote, edit details, manage plans, view disciplinary logs." },
  { to: "/admin?tab=companies",    label: "Companies",           Icon: BadgeCheck, blurb: "Approve recruiters, transfer ownership, set branding." },
  { to: "/admin?tab=invites",      label: "Invites",             Icon: Send,      blurb: "Bulk-grant paid plans, lock invites to a company, share via email." },
  { to: "/settings?tab=platform",  label: "Platform branding",   Icon: Palette,   blurb: "Site-wide name, tagline, logo, and colours." },
];

export default function HRPortal() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const isRecruiter = user?.role === "recruiter";
  const [boosts, setBoosts] = useState(null);
  useEffect(() => {
    if (isRecruiter || isOwner) api.get("/recruiter/limits").then((r) => setBoosts(r.data?.boosts)).catch(() => {});
  }, [isRecruiter, isOwner]);

  const tiles = isOwner ? [...TILES, ...OWNER_TILES] : TILES;

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <div>
        <div className="label-eyebrow flex items-center gap-1.5"><BadgeCheck className="w-3 h-3 text-[var(--brand-primary)]" /> HR Portal</div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tight mt-3" data-testid="hr-title">Everything HR, in one place.</h1>
        <p className="text-[#5C5C5C] mt-3 max-w-xl">Hiring, branding, disciplinary records, billing, plan inheritance, and platform compliance — all linked from this one hub.</p>
      </div>

      {boosts && (
        <div className="mt-10 border border-[#0F0F0F] p-4 text-sm flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="hr-boost-summary">
          <span className="label-eyebrow">Free this month</span>
          <span>
            <strong>Featured</strong> {boosts.featured.remaining === -1 ? "∞" : `${boosts.featured.remaining}/${boosts.featured.allowance}`}
          </span>
          <span>
            <strong>Premium</strong> {boosts.premium.remaining === -1 ? "∞" : `${boosts.premium.remaining}/${boosts.premium.allowance}`}
          </span>
        </div>
      )}

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
        {tiles.map((t) => (
          <Link key={t.to} to={t.to} data-testid={`hr-tile-${t.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            className="bg-[#FDFCF8] p-6 hover:bg-[#0F0F0F]/5 transition-colors group">
            <div className="flex items-start justify-between gap-3">
              <t.Icon className="w-6 h-6 text-[var(--brand-primary)] flex-shrink-0" />
              <ArrowRight className="w-4 h-4 text-[#5C5C5C] group-hover:text-[var(--brand-primary)] group-hover:translate-x-1 transition-all" />
            </div>
            <div className="font-display font-black text-xl mt-4">{t.label}</div>
            <p className="text-sm text-[#5C5C5C] mt-1.5">{t.blurb}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
